import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuthContext } from '../contexts/AuthContext';

interface CalendarSyncSettings {
  id: string;
  provider: 'google' | 'outlook' | 'apple';
  sync_enabled: boolean;
  alert_minutes_before: number[];
  last_synced_at: Date | null;
}

export function useCalendarSync() {
  const { user } = useAuthContext();
  const { hasFeature } = useSubscription();
  const [settings, setSettings] = useState<CalendarSyncSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user && hasFeature('calendar_sync')) {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_sync_settings')
        .select('*')
        .eq('user_id', user?.id);

      if (error) throw error;
      setSettings(data || []);
    } catch (err) {
      console.error('Error fetching calendar sync settings:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (
    provider: CalendarSyncSettings['provider'],
    updates: Partial<CalendarSyncSettings>
  ) => {
    try {
      const { error } = await supabase
        .from('calendar_sync_settings')
        .upsert({
          user_id: user?.id,
          provider,
          ...updates
        }, {
          onConflict: 'user_id,provider'
        });

      if (error) throw error;
      await fetchSettings();
    } catch (err) {
      console.error('Error updating calendar sync settings:', err);
      throw err;
    }
  };

  const deleteSettings = async (provider: CalendarSyncSettings['provider']) => {
    try {
      const { error } = await supabase
        .from('calendar_sync_settings')
        .delete()
        .eq('user_id', user?.id)
        .eq('provider', provider);

      if (error) throw error;
      await fetchSettings();
    } catch (err) {
      console.error('Error deleting calendar sync settings:', err);
      throw err;
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings,
    deleteSettings
  };
}